import { h } from 'preact';
import { useEffect, useRef, useState, useLayoutEffect } from 'preact/hooks';
import useResponsive from 'utils/responsive';
import PlayIcon from 'utils/icon/icon-play.svg';
import PauseIcon from 'utils/icon/icon-pause.svg';
import SoundOnIcon from 'utils/icon/icon-sound-on.svg';
import SoundOffIcon from 'utils/icon/icon-sound-off.svg';
import useUniqueId from 'utils/unique-id';
import clsx from 'clsx';


const VimeoPlayer = ({
  originUrl,
  onClick,
  className,
  previewImage,
  controlClass = '',
  slideId,
  currentSlide,
  useVideoRatio = false,
  autoPlay = false,
  enableMute = false,
  isQuickView,
  activeIndex
}) => {

  const id = useUniqueId();
  const { isMobile } = useResponsive();

  const [paused, setPaused] = useState(!autoPlay && isFullScreen || !isMobile);
  const [muted, setMuted] = useState(true);

  const videoFrame = useRef(null);
  const player = useRef(null);

  const isFullScreen = activeIndex !== null;
  const vimeoId = originUrl.split('/video/')[1]; // '288344114';


  const initVideo = () => {
    player.current = new window.Vimeo.Player(videoFrame.current);

    if (useVideoRatio) {
      Promise.all([
        player.current.getVideoWidth(),
        player.current.getVideoHeight(),
      ]).then((dimensions) => {
        if (dimensions && dimensions.length) {
          const [width, height] = dimensions;
          videoFrame.current.parentElement.style.paddingTop =
            `${(height / width) * 100}%`;
        }
      });
    }

    player.current.on('pause', () => {
      setPaused(true);
    });

    player.current.on('play', () => {
      setPaused(false);
    });

  };

  useLayoutEffect(() => {
    if (useVideoRatio && previewImage?.width && previewImage?.height) {
      videoFrame.current.parentElement.style.paddingTop =
        `${(previewImage.height / previewImage.width) * 100}%`;
    }
    if (window.Vimeo) {
      initVideo();
    } else if (document.getElementById('vimeo-script')) {
      document.addEventListener('vimeoLoaded', initVideo);
    } else {
      const script = document.createElement('script');
      script.setAttribute('src', 'https://player.vimeo.com/api/player.js');
      script.setAttribute('id', 'vimeo-script');
      document.body.appendChild(script);
      script.onload = () => {
        initVideo();
        document.dispatchEvent(new CustomEvent('vimeoLoaded'));
      };
    }
    return () => {
      document.removeEventListener('vimeoLoaded', initVideo);
    };
  }, []);


  useEffect(() => {
    // pause when full screen
    if (isFullScreen) {
      setPaused(true);
      setMuted(true);
    }

    // auto play on mobile devices
    if (!isFullScreen && currentSlide === slideId) {
      setPaused(false);
      setMuted(true);
    }
  }, [isFullScreen, currentSlide]);

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  let url = `https://player.vimeo.com/video/${vimeoId}`
    + `?speed=0`
    + `&pip=0`
    + `&controls=0`
    + `&loop=0`
    + `&autopause=1`
    + `&muted=1`;
  url += + (!isFullScreen && autoPlay || isMobile) ? `&autoplay=1` : '&autoplay=0';

  const toggleVideo = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setPaused((active) => !active);
  };

  const toggleSound = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setMuted((active) => !active);
  };

  useEffect(() => {
    if (paused) {
      player.current?.pause();
    } else {
      player.current?.play();
    }
  }, [paused]);

  useEffect(() => {
    if (muted) {
      player.current?.setVolume(0);
    } else {
      player.current?.setVolume(1);
    }
  }, [muted]);

  const mouseOver = (event) => {
    if (!event.target.closest('[data-section="product"], .quick-shop')) {
      setPaused(false);
    }
  };



  return (
    <div
      className={clsx('vimeo [&_div]:!static', {
        '[&_div]:!p-0 [&_iframe]:bg-grey-300': !useVideoRatio,
        relative: useVideoRatio,
      })}
    >
      {useVideoRatio && !!previewImage?.src && (
        <img
          src={previewImage.src}
          className="object-cover absolute inset-0 w-full h-full"
        />
      )}
      <iframe
        src={url}
        id={id}
        ref={videoFrame}
        className={`absolute inset-0 w-full h-full ${className}`}
        allow={"accelerometer; encrypted-media; gyroscope; picture-in-picture; autoplay"}
        allowFullScreen={"allowfullscreen"}
      ></iframe>
      <span
        className="absolute inset-0"
        onClick={handleClick}
        onMouseOver={mouseOver}
      ></span>
      <button
        className={`
          z-10 absolute top-16 left-16
          ${isFullScreen
            ? `md:right-33 ml:md:right-40 
                ${isQuickView ?
              `ml:top-[85%] ml:left-[5%] lc:top-[80%] lc:left-[5%]` :
              `md:top-128 md:left-auto`
            }`
            : `md:left-8 md:bottom-8 md:top-auto`
          }
          lc:w-36 lc:h-36 ml:w-24 ml:h-24 lc:rounded-full
          bg-white flex items-center justify-center ${controlClass}
        `}
        onClick={toggleVideo}
      >
        {!paused && <PauseIcon className="w-18 h-auto" />}
        {paused && <PlayIcon className="w-18 h-auto" />}
      </button>
      {enableMute && (
        <button
          className={`
            z-10 absolute top-16 left-62 md:left-52 ml:left-52
            ${isFullScreen
              ? `md:top-176 md:right-33 md:left-auto
                   ml:md:top-160 ml:md:right-40 ml:md:left-auto`
              : 'ml:md:left-40 md:bottom-8 md:top-auto'
            }
            lc:w-36 lc:h-36 ml:w-24 ml:h-24 lc:rounded-full
            bg-white flex items-center justify-center ${controlClass}
          `}
          onClick={toggleSound}
        >
          {muted ? (
            <SoundOffIcon className="w-24 h-auto" />
          ) : (
            <SoundOnIcon className="w-24 h-auto" />
          )}
        </button>
      )}
    </div>
  );
};

export default VimeoPlayer;
